import React, { useState, useEffect, useMemo } from "react";
import {
  ItemYield,
  UpdateItemParams,
  CalculateCostsResponseSchema,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Lock, Loader } from "lucide-react";
import { fetchApi } from "@/lib/api";
import Decimal from "decimal.js";
import { YieldEditorSheet } from "@/modules/items/YieldEditorSheet";

interface BOMYieldEditorProps {
  itemId: string;
  yields: ItemYield[];
  quantities: string[];
  onUpdate: (updates: UpdateItemParams) => Promise<true | string>;
  scrollRef?: (el: HTMLDivElement | null) => void;
  onScrollSync?: React.UIEventHandler<HTMLDivElement>;
  /** When true, yield editing is disabled (controlled by a price model). */
  locked?: boolean;
}

function getActiveYieldForQuantity(
  yields: ItemYield[],
  qty: number,
): ItemYield | undefined {
  if (yields.length === 0) {
    return undefined;
  }

  const applicableQuantityRates = yields.filter(
    (r) => r.minQty !== undefined && r.minQty <= qty,
  );

  if (applicableQuantityRates.length > 0) {
    return applicableQuantityRates.reduce((prev, current) =>
      (current.minQty ?? 0) > (prev.minQty ?? 0) ? current : prev,
    );
  }

  const defaultRate = yields.find((r) => r.minQty === undefined);
  if (defaultRate) {
    return defaultRate;
  }

  return undefined;
}

function calculateQuantityToMake(qty: string, percentage: string): string {
  const qtyDecimal = new Decimal(qty);
  const pctDecimal = new Decimal(percentage);
  const factor = pctDecimal.dividedBy(100);

  if (factor.lessThanOrEqualTo(0)) {
    return qty;
  }

  return qtyDecimal.dividedBy(factor).ceil().toString();
}

export function BOMYieldEditor({
  itemId,
  yields,
  quantities,
  onUpdate,
  scrollRef,
  onScrollSync,
  locked = false,
}: BOMYieldEditorProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [quantityToMakeData, setQuantityToMakeData] = useState<
    Map<string, string>
  >(new Map());
  const [isLoadingData, setIsLoadingData] = useState(false);

  const handleSaveYields = async (newYields: ItemYield[]) => {
    await onUpdate({ yield: newYields });
  };

  useEffect(() => {
    const fetchQuantityToMake = async () => {
      if (!itemId || yields.length === 0) {
        setQuantityToMakeData(new Map());
        return;
      }

      setIsLoadingData(true);
      try {
        const response = await fetchApi(
          `items/${itemId}/calculate-costs`,
          "POST",
          { body: { quantities } },
        );

        if (response.ok && response.data) {
          const { costs } = CalculateCostsResponseSchema.parse(response.data);
          const dataMap = new Map<string, string>();
          for (const cost of costs) {
            if (cost.quantityToMake) {
              dataMap.set(cost.quantity, cost.quantityToMake);
            }
          }
          setQuantityToMakeData(dataMap);
        }
      } catch {
        setQuantityToMakeData(new Map());
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchQuantityToMake();
  }, [itemId, yields, quantities]);

  const yieldRows = useMemo(() => {
    const defaultRate = yields.find((r) => r.minQty === undefined);
    const overrides = yields
      .filter((r) => r.minQty !== undefined)
      .sort((a, b) => (a.minQty ?? 0) - (b.minQty ?? 0));

    const sorted: ItemYield[] = [];
    if (defaultRate) {
      sorted.push(defaultRate);
    }
    sorted.push(...overrides);

    return sorted.map((rate, index) => ({
      id: `yield-row-${index}`,
      label:
        rate.minQty === undefined ? "Yield" : `Yield (at Qty ${rate.minQty})`,
      valueLabel: `${rate.percentage}%`,
      rate,
    }));
  }, [yields]);

  if (yields.length === 0) {
    return (
      <>
        <div
          ref={scrollRef}
          onScroll={onScrollSync}
          className="bg-card rounded-lg border shadow-sm overflow-x-auto"
        >
          <table
            className="w-full border-separate border-spacing-0 table-fixed"
            style={{ minWidth: `${300 + quantities.length * 120}px` }}
          >
            <thead className="bg-muted">
              <tr>
                <th className="text-left py-2 px-4 text-sm font-semibold w-[300px] min-w-[300px] sticky left-0 z-20 bg-muted border-r border-border border-b border-b-border">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      Yield
                      {locked && <Lock className="h-3 w-3 text-amber-500" />}
                    </span>
                    {!locked && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsSheetOpen(true)}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </th>
                {quantities.map((qty) => (
                  <th
                    key={qty}
                    className="text-right py-2 px-4 text-sm font-semibold min-w-[100px] border-l border-border border-b border-b-border"
                  >
                    Qty {qty}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4 text-sm text-muted-foreground sticky left-0 z-10 bg-card border-r border-border border-b border-b-border">
                  No yield configured
                </td>
                {quantities.map((qty) => (
                  <td
                    key={qty}
                    className="py-2 px-4 text-right text-sm border-l border-border border-b border-b-border text-muted-foreground"
                  >
                    —
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <YieldEditorSheet
          open={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          yields={yields}
          onSave={handleSaveYields}
        />
      </>
    );
  }

  return (
    <>
      <div
        ref={scrollRef}
        onScroll={onScrollSync}
        className="bg-card rounded-lg border shadow-sm overflow-x-auto"
      >
        <table
          className="w-full border-separate border-spacing-0 table-fixed"
          style={{ minWidth: `${300 + quantities.length * 120}px` }}
        >
          <thead className="bg-muted">
            <tr>
              <th className="text-left py-2 px-4 text-sm font-semibold w-[300px] min-w-[300px] sticky left-0 z-20 bg-muted border-r border-border border-b border-b-border">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    Yield
                    {locked && <Lock className="h-3 w-3 text-amber-500" />}
                  </span>
                  {!locked && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsSheetOpen(true)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </th>
              {quantities.map((qty) => (
                <th
                  key={qty}
                  className="text-right py-2 px-4 text-sm font-semibold min-w-[100px] border-l border-border border-b border-b-border"
                >
                  Qty {qty}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {yieldRows.map((row) => {
              const rate = row.rate;
              const isDefault = rate.minQty === undefined;

              return (
                <tr key={row.id} className={!isDefault ? "bg-row-stripe" : ""}>
                  <td
                    className={`py-2 px-4 text-sm sticky left-0 z-10 border-r border-border border-b border-b-border ${!isDefault ? "pl-8 bg-row-stripe" : "bg-card"}`}
                  >
                    {!isDefault && (
                      <span className="text-muted-foreground mr-2">
                        &#8627;
                      </span>
                    )}
                    {row.label} ({row.valueLabel})
                  </td>
                  {quantities.map((qty) => {
                    const qtyNum = parseInt(qty, 10);
                    const activeRate = getActiveYieldForQuantity(
                      yields,
                      qtyNum,
                    );
                    const isActive = activeRate === rate;

                    const qtyToMakeFromServer = quantityToMakeData.get(qty);
                    const qtyToMakeClientSide = calculateQuantityToMake(
                      qty,
                      rate.percentage,
                    );
                    const displayQtyToMake =
                      isActive && qtyToMakeFromServer
                        ? qtyToMakeFromServer
                        : qtyToMakeClientSide;

                    return (
                      <td
                        key={qty}
                        className={`py-2 px-4 text-right text-sm border-l border-border border-b border-b-border tabular-nums ${
                          isActive
                            ? "bg-accent font-medium"
                            : "text-muted-foreground"
                        }`}
                      >
                        {isLoadingData ? (
                          <Loader className="h-4 w-4 animate-spin inline" />
                        ) : isActive ? (
                          <>
                            <div>Make {displayQtyToMake}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {rate.percentage}% yield
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <YieldEditorSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        yields={yields}
        onSave={handleSaveYields}
      />
    </>
  );
}
